import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { apiRequest, apiUrl } from "../../../helpers/api";
import { getToken } from "../../../helpers/authHelper";
import { MARKETING_URL } from "../../../config";
import { showError, showSuccess } from "../../../components/ToastNotifications";

// The public marketing site. "/" means it shares this origin (the development proxy on port 3000).
export const siteUrl = (path = "/") => (MARKETING_URL === "/" ? path : `${MARKETING_URL}${path}`);

// Uploaded images live on the API; other site-relative images ship with the marketing site.
export const assetSrc = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return apiUrl(url);
  if (url.startsWith("/")) return siteUrl(url);
  return url;
};

export const formatDateTime = (value) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "";

const supportedImageTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export const uploadImage = async (file) => {
  if (!supportedImageTypes.includes(file.type)) throw new Error(`${file.name}: upload a JPG, PNG, WebP or GIF image.`);
  if (file.size > 10 * 1024 * 1024) throw new Error(`${file.name} is larger than 10 MB.`);
  let response;
  try {
    response = await fetch(apiUrl("/api/admin/website/media"), {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken() || ""}`, "Content-Type": "application/octet-stream", "X-File-Name": encodeURIComponent(file.name) },
      body: file
    });
  } catch {
    throw new Error("Couldn't reach the server to upload the image.");
  }
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || "Unable to upload the image.");
  return result.asset;
};

// Wires a label, hint/error and optional character counter to the single control passed as children.
export const Field = ({ label, error, hint, count, max, children, className = "" }) => {
  const id = useId();
  const messageId = `${id}-message`;
  const over = max && count > max;
  return <div className={`cms-field${error ? " has-error" : ""} ${className}`}>
    <div className="cms-field-label">
      <label htmlFor={id}>{label}</label>
      {count !== undefined && max ? <span className={`cms-counter${over ? " is-over" : ""}`}>{count}/{max}</span> : null}
    </div>
    {React.cloneElement(children, { id, "aria-invalid": error ? true : undefined, "aria-describedby": error || hint ? messageId : undefined })}
    {error ? <small id={messageId} className="cms-field-error"><i className="ti ti-alert-circle" aria-hidden="true" />{error}</small> : hint ? <small id={messageId} className="cms-field-hint">{hint}</small> : null}
  </div>;
};

export const Toggle = ({ checked, onChange, label, disabled, hint }) => <label className={`cms-toggle${disabled ? " is-disabled" : ""}`}>
  <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
  <span className="cms-switch" aria-hidden="true" />
  <span className="cms-toggle-copy"><span>{label}</span>{hint && <small>{hint}</small>}</span>
</label>;

// Modal dialog: traps focus loosely (initial focus + Escape) and restores focus on close.
export const Dialog = ({ title, onClose, children, footer, wide, labelledBy }) => {
  const ref = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const first = ref.current?.querySelector("input, select, textarea, button:not(.cms-dialog-close)");
    (first || ref.current)?.focus();
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && previous.focus) previous.focus();
    };
  }, [onClose]);
  return <div className="cms-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={ref} tabIndex={-1} className={`cms-dialog${wide ? ` is-${wide === true ? "wide" : wide}` : ""}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy || titleId}>
      <header className="cms-dialog-header">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="cms-icon-button cms-dialog-close" onClick={onClose} aria-label="Close"><i className="ti ti-x" aria-hidden="true" /></button>
      </header>
      <div className="cms-dialog-body">{children}</div>
      {footer && <footer className="cms-dialog-footer">{footer}</footer>}
    </section>
  </div>;
};

export const ConfirmDialog = ({ title, message, confirmLabel, danger, busy, onConfirm, onClose }) => <Dialog title={title} onClose={onClose} footer={<>
  <button type="button" className="cms-button" onClick={onClose} disabled={busy}>Cancel</button>
  <button type="button" className={`cms-button ${danger ? "is-danger" : "is-primary"}`} onClick={onConfirm} disabled={busy}>
    {busy && <i className="ti ti-loader-2 cms-spin" aria-hidden="true" />}{confirmLabel}
  </button>
</>}>
  <p className="cms-dialog-text">{message}</p>
</Dialog>;

export const ImageLibraryDialog = ({ onSelect, onClose, canUpload, canDelete }) => {
  const [assets, setAssets] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      setAssets((await apiRequest("/api/admin/website/media")).assets);
    } catch (loadError) {
      setError(loadError.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setUploading(true);
    try {
      let last = null;
      for (const file of files) last = await uploadImage(file);
      await load();
      if (last && files.length === 1) onSelect(last);
      else showSuccess("Images uploaded.");
    } catch (uploadError) {
      showError(uploadError.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (asset) => {
    setDeleting(asset.id);
    try {
      await apiRequest(`/api/admin/website/media/${asset.id}`, { method: "DELETE" });
      setAssets((current) => current.filter((item) => item.id !== asset.id));
      showSuccess("Image deleted.");
    } catch (deleteError) {
      showError(deleteError.message);
    } finally {
      setDeleting(null);
    }
  };

  return <Dialog title="Website images" onClose={onClose} wide footer={<>
    <span className="cms-muted">Images are converted to WebP and optimized automatically.</span>
    {canUpload && <>
      <input ref={inputRef} type="file" accept={supportedImageTypes.join(",")} multiple hidden onChange={(event) => event.target.files.length && upload([...event.target.files])} />
      <button type="button" className="cms-button is-primary" onClick={() => inputRef.current?.click()} disabled={uploading}>
        <i className={`ti ${uploading ? "ti-loader-2 cms-spin" : "ti-upload"}`} aria-hidden="true" />{uploading ? "Uploading…" : "Upload images"}
      </button>
    </>}
  </>}>
    {error && <div className="cms-alert is-error" role="alert"><span>{error}</span><button type="button" className="cms-button" onClick={load}>Retry</button></div>}
    {!assets && !error && <p className="cms-muted" role="status">Loading images…</p>}
    {assets && !assets.length && <div className="cms-empty"><i className="ti ti-photo" aria-hidden="true" /><strong>No images yet</strong><span>{canUpload ? "Upload an image to use it on the website." : "Images uploaded for the website will appear here."}</span></div>}
    {assets?.length > 0 && <ul className="cms-library-grid">
      {assets.map((asset) => <li key={asset.id}>
        <button type="button" className="cms-library-item" onClick={() => onSelect(asset)} aria-label={`Use ${asset.originalName}`}>
          <img src={assetSrc(asset.url)} alt="" loading="lazy" />
          <span><strong>{asset.originalName}</strong><small>{asset.width}×{asset.height} · {Math.max(1, Math.round(asset.size / 1024))} KB</small></span>
        </button>
        {canDelete && <button type="button" className="cms-icon-button cms-library-delete" onClick={() => remove(asset)} disabled={deleting === asset.id} aria-label={`Delete ${asset.originalName}`} title="Delete image">
          <i className={`ti ${deleting === asset.id ? "ti-loader-2 cms-spin" : "ti-trash"}`} aria-hidden="true" />
        </button>}
      </li>)}
    </ul>}
  </Dialog>;
};

// Image with alt text: upload, pick from the library, or paste an https:// address.
export const ImageField = ({ label, value, onChange, errors = {}, required, disabled, canUpload, canDelete, withAlt = true }) => {
  const image = value || { url: "", alt: "" };
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const set = (changes) => onChange({ ...image, ...changes });

  const upload = async (file) => {
    setUploading(true);
    try {
      const asset = await uploadImage(file);
      set({ url: asset.url });
    } catch (uploadError) {
      showError(uploadError.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return <fieldset className="cms-image-field" disabled={disabled}>
    <legend>{label}{required && <span className="cms-required" aria-hidden="true"> *</span>}</legend>
    <div className="cms-image-row">
      <div className={`cms-image-thumb${image.url ? "" : " is-empty"}`}>
        {image.url ? <img src={assetSrc(image.url)} alt="" /> : <i className="ti ti-photo" aria-hidden="true" />}
      </div>
      <div className="cms-image-actions">
        {canUpload && <>
          <input ref={inputRef} type="file" accept={supportedImageTypes.join(",")} hidden onChange={(event) => event.target.files[0] && upload(event.target.files[0])} />
          <button type="button" className="cms-button is-small" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <i className={`ti ${uploading ? "ti-loader-2 cms-spin" : "ti-upload"}`} aria-hidden="true" />{uploading ? "Uploading…" : "Upload"}
          </button>
        </>}
        <button type="button" className="cms-button is-small" onClick={() => setLibraryOpen(true)}><i className="ti ti-photo-search" aria-hidden="true" />Library</button>
        {image.url && <button type="button" className="cms-button is-small is-ghost" onClick={() => set({ url: "" })}><i className="ti ti-x" aria-hidden="true" />Remove</button>}
      </div>
    </div>
    <Field label="Image address" error={errors.url} hint="Uploaded images fill this in. You can also paste an https:// image URL.">
      <input value={image.url} onChange={(event) => set({ url: event.target.value.trim() })} placeholder="https://…" maxLength={500} spellCheck={false} />
    </Field>
    {withAlt && <Field label="Alt text" error={errors.alt} hint="Describe the image for screen readers and search engines. Leave empty only for decorative images." count={image.alt.length} max={200}>
      <input value={image.alt} onChange={(event) => set({ alt: event.target.value })} maxLength={200} />
    </Field>}
    {libraryOpen && <ImageLibraryDialog canUpload={canUpload} canDelete={canDelete} onClose={() => setLibraryOpen(false)} onSelect={(asset) => { set({ url: asset.url }); setLibraryOpen(false); }} />}
  </fieldset>;
};

